import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useMarkdownLinkPreview } from './useMarkdownLinkPreview';
import { formatLinkHostname, type LinkPreview } from '../utils/linkThumbnail';

export type MarkdownLinkVariant = 'default' | 'card';

const linkPreviewCardClasses: Record<MarkdownLinkVariant, string> = {
  default:
    'my-2 flex max-w-md flex-col overflow-hidden rounded-md border bg-card text-foreground no-underline transition-colors hover:bg-muted/40',
  card: 'my-3 flex max-w-md flex-col overflow-hidden rounded-xl border bg-card text-foreground no-underline shadow-sm transition-colors hover:bg-muted/40',
};

type MarkdownLinkProps = {
  href?: string;
  children?: React.ReactNode;
  variant?: MarkdownLinkVariant;
};

function getLinkText(children: React.ReactNode): string {
  return React.Children.toArray(children)
    .map(child => {
      if (typeof child === 'string' || typeof child === 'number') {
        return String(child);
      }
      return '';
    })
    .join('')
    .trim();
}

function getDisplayLabel(
  children: React.ReactNode,
  href: string,
  preview?: LinkPreview | null
): string {
  if (preview?.title) {
    return preview.title;
  }
  const text = getLinkText(children);
  if (text && text !== href) {
    return text;
  }
  return formatLinkHostname(href);
}

const PlainMarkdownLink: React.FC<MarkdownLinkProps> = ({ href, children }) => (
  <a
    href={href}
    className='text-primary underline underline-offset-2 hover:opacity-80'
    target='_blank'
    rel='noopener noreferrer'
  >
    {children}
  </a>
);

export const MarkdownLink: React.FC<MarkdownLinkProps> = ({
  href,
  children,
  variant = 'default',
}) => {
  const cachedPreview = useMarkdownLinkPreview(href);
  const preview: LinkPreview | null =
    cachedPreview === undefined ? null : cachedPreview;
  const [thumbnailFailed, setThumbnailFailed] = useState(false);

  useEffect(() => {
    setThumbnailFailed(false);
  }, [href, preview?.thumbnailUrl]);

  if (!href || !preview?.thumbnailUrl || thumbnailFailed) {
    return <PlainMarkdownLink href={href}>{children}</PlainMarkdownLink>;
  }

  const label = getDisplayLabel(children, href, preview);
  const hostname = formatLinkHostname(href);

  return (
    <a
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      className={linkPreviewCardClasses[variant]}
    >
      <img
        src={preview.thumbnailUrl}
        alt=''
        loading='lazy'
        className='h-36 w-full object-cover'
        onError={() => {
          console.error(
            'Failed to load link preview thumbnail:',
            preview.thumbnailUrl
          );
          setThumbnailFailed(true);
        }}
      />
      <span className='flex items-start gap-2 px-3 py-2'>
        <span className='min-w-0 flex-1'>
          <span className='block truncate text-sm font-medium text-foreground'>
            {label}
          </span>
          <span className='block truncate text-xs text-muted-foreground'>
            {hostname}
          </span>
        </span>
        <ExternalLink
          className='mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground'
          aria-hidden='true'
        />
      </span>
    </a>
  );
};
